import React, { useEffect, useMemo, useRef, useState } from "react";
import { Field, formValueSelector, reduxForm } from "redux-form";
import { CircularProgress } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { notification } from "antd";
import { CustomSwitch } from "../../../../../../common/customSwitch";
import { saveJobSwmsEmergencyPlans } from "../../../../../../../actions/v2/JobSwmsEmergencyPlansActions";
import { Strings } from "../../../../../../../dataProvider/localize";
import { normalizeEmergencyPlans, serializeEmergencyPlan } from "./emergencyPlanAdapter";
import styles from "./JobSwmsV3EmergencyPlansContainer.module.css";
import jobStyles from "../../../../JobStyle.module.css";
import JobSwmsV3EmergencyPlanSectionRegistry from "./editor/JobSwmsV3EmergencyPlanSectionRegistry";
import { buildEmergencyPlansEditItems, createSwmsEditContext } from "../editHistory/swmsEditContext";

const getPlanKey = (plan = {}) => plan.id !== undefined && plan.id !== null
  ? plan.id
  : (plan.document_number || (plan.source && plan.source.plan_id));
const FORM_NAME = "JobSwmsV3EmergencyPlansContainerForm";
const selector = formValueSelector(FORM_NAME);

const getNotificationText = (value, fallback) => {
  if (typeof value === "string") {
    return value;
  }

  if (value && typeof value.message === "string") {
    return value.message;
  }

  return fallback;
};

const planToggleField = ({ input, ...props }) => (
  <CustomSwitch
    {...props}
    input={{
      ...input,
      onChange: (checked) => input.onChange(checked ? 1 : 0)
    }}
    size="small"
  />
);

function JobSwmsV3EmergencyPlansContainer({
  serverState = {},
  onRetry,
  handleSubmit,
  categories = [],
  swmsStatus,
  swmsData = {},
  methodItemTypeGroups = [],
  methodItemTypesLoading = false,
  jobId,
  swmsId,
  version,
  toggle = true,
  onClose,
  refreshForm
  ,saveRequest = 0
  ,discardRequest = 0
  ,reset
  ,pristine
  ,onDirtyChange
  ,onSavingChange
}) {
  const dispatch = useDispatch();
  const [saving, setSaving] = useState(false);
  const lastDiscardRequest = useRef(0);
  useEffect(() => {
    if (saveRequest) handleSubmit(onSubmit)();
  }, [saveRequest]);
  useEffect(() => {
    if (discardRequest && discardRequest !== lastDiscardRequest.current) {
      lastDiscardRequest.current = discardRequest;
      reset();
    }
  }, [discardRequest, reset]);
  useEffect(() => {
    if (onDirtyChange) onDirtyChange(!pristine);
  }, [pristine, onDirtyChange]);

  const rawItems = Array.isArray(serverState.data)
    ? serverState.data
    : (serverState.data && Array.isArray(serverState.data.plans)
      ? serverState.data.plans
      : (serverState.document && Array.isArray(serverState.document.plans)
        ? serverState.document.plans
        : []));
  const items = useMemo(() => normalizeEmergencyPlans(rawItems), [rawItems]);
  const formPlans = useSelector((state) => selector(state, "plans") || []);
  const [selectedId, setSelectedId] = useState(null);
  const error = serverState.error;

  useEffect(() => {
    if (items.length > 0 && !items.some((item) => `${getPlanKey(item)}` === `${selectedId}`)) {
      setSelectedId(getPlanKey(items[0]));
    }
  }, [items, selectedId]);

  const selected = items.find(
    (item) => `${getPlanKey(item)}` === `${selectedId}`
  ) || items[0];

  if (serverState.loading) {
    return (
      <div className={styles.PlansContainer}>
        <div className={styles.LoadingState}>
          <CircularProgress size={18} />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.PlansContainer}>
        <div className={styles.ErrorState}>
          <div>{error}</div>
          <button type="button" className={styles.RetryButton} onClick={onRetry}>
            Retry
          </button>
        </div>
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <div className={styles.PlansContainer}>
        <div className={styles.EmptyState}>No emergency plans yet.</div>
      </div>
    );
  }

  const onSubmit = (values) => {
    if (saving) return;

    const plans = (values.plans || []).map((plan, index) =>
      serializeEmergencyPlan(plan, items[index] || {}, methodItemTypeGroups)
    );
    const historyPlans = (values.plans || []).map((plan, index) => {
      const serializedPlan = plans[index] || {};
      const serializedSections = Array.isArray(serializedPlan.sections)
        ? serializedPlan.sections.map((section) => {
          if (!section
            || (section.id !== "roles_responsibilities_competency"
              && section.id !== "alarm_communication_emergency_access")) {
            return section;
          }
          const sourceItems = plan
            && plan.sections
            && plan.sections[section.id]
            && plan.sections[section.id].data
            && Array.isArray(plan.sections[section.id].data.items)
            ? plan.sections[section.id].data.items
            : [];
          return {
            ...section,
            data: {
              ...section.data,
              items: (Array.isArray(section.data && section.data.items) ? section.data.items : [])
                .map((item, itemIndex) => ({
                  ...item,
                  _history_id: sourceItems[itemIndex] && sourceItems[itemIndex]._history_id
                }))
            }
          };
        })
        : serializedPlan.sections;

      return {
        ...serializedPlan,
        sections: serializedSections
      };
    });
    const editItems = buildEmergencyPlansEditItems(items, historyPlans);
    const documentName = editItems.length === 0
      ? "All Emergency Plans"
      : (editItems.length === 1
        ? editItems[0].item_label || "All Emergency Plans"
        : "Multiple Emergency Plans");
    const editContext = createSwmsEditContext({
      initialValues: { plans: items },
      submittedValues: { plans },
      internalRevisionId: serverState.internal_revision_id,
      documentName,
      documentType: "emergency_plans",
      items: editItems
    });
    setSaving(true);
    if (onSavingChange) onSavingChange(true);
    dispatch(saveJobSwmsEmergencyPlans(jobId, swmsId, plans, version, undefined, editContext))
      .then((message) => {
        notification.success({
          message: Strings.success_title,
          description: getNotificationText(message, "Emergency plans saved successfully."),
          className: "ant-success"
        });
        return refreshForm ? refreshForm({ closeOnError: false, showBlockingLoader: false }) : null;
      })
      .then(() => {
        setSaving(false);
        if (onSavingChange) onSavingChange(false);
      })
      .catch((err) => {
        setSaving(false);
        if (onSavingChange) onSavingChange(false);
        notification.error({
          message: Strings.error_title,
          description: getNotificationText(err, Strings.generic_error),
          className: "ant-error"
        });
      });
  };

  return (
    <form className={styles.PlansContainer} onSubmit={handleSubmit(onSubmit)}>
      <div className="container-fluid py-3">
        <div className="row">
          <div className="col-12">
            <div className={styles.PlanTabRow} role="tablist" aria-label="Emergency Plans">
              {items.map((item, index) => {
                const key = getPlanKey(item);
                const isSelected = `${key}` === `${getPlanKey(selected)}`;
                const isIncluded = Number(
                  formPlans[index] && formPlans[index].toggle !== undefined
                    ? formPlans[index].toggle
                    : item.toggle
                ) === 1;

                return (
                  <button
                    type="button"
                    role="tab"
                    aria-selected={isSelected}
                    className={`${styles.PlanTab} ${isSelected ? styles.PlanTabActive : ""} ${!isIncluded ? styles.PlanTabOff : ""}`.trim()}
                    key={key || index}
                    onClick={() => setSelectedId(key)}
                  >
                    <span className={styles.PlanName}>{item.plan_name}</span>
                    <span
                      className={styles.PlanToggle}
                      onClick={(event) => event.stopPropagation()}
                      onMouseDown={(event) => event.stopPropagation()}
                    >
                      <Field
                        name={`plans[${index}].toggle`}
                        component={planToggleField}
                      />
                    </span>
                    {!isIncluded ? (
                      <span className={styles.PlanTabOffBadge}>OFF</span>
                    ) : null}
                  </button>
                );
              })}
            </div>
            <JobSwmsV3EmergencyPlanSectionRegistry
              key={getPlanKey(selected)}
              planIndex={items.findIndex((item) => getPlanKey(item) === getPlanKey(selected))}
              categories={categories}
              documentNumber={selected.document_number}
              swmsStatus={swmsStatus}
              swmsData={swmsData}
              loading={serverState.loading}
              selectedPlan={selected}
              methodItemTypeGroups={methodItemTypeGroups}
              methodItemTypesLoading={methodItemTypesLoading}
            />
          </div>
        </div>
      </div>
    </form>
  );
}

export default reduxForm({
  form: "JobSwmsV3EmergencyPlansContainerForm",
  destroyOnUnmount: true,
  enableReinitialize: true
})(JobSwmsV3EmergencyPlansContainer);